import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { CheckCircle, Loader2, AlertCircle, Leaf } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { createProfile, hasCompletedOnboarding } from "../lib/api";
import { toast } from "sonner";

interface EmailVerificationProps {
  onNavigate?: (page: string) => void;
}

export function EmailVerification({ onNavigate }: EmailVerificationProps) {
  const { user, loading: authLoading } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authTimeout, setAuthTimeout] = useState(false);

  // Check for auth errors in URL
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const combined = hash + search;
    
    console.log('🔍 EmailVerification mounted - checking URL for auth tokens/errors');
    console.log('Hash:', hash.substring(0, 100));
    console.log('Search:', search);
    
    if (combined.includes('error=')) {
      const params = new URLSearchParams(hash.split('?')[1] || search.slice(1));
      const errorType = params.get('error');
      const errorDesc = params.get('error_description');
      
      console.error('❌ Auth error from Supabase:', errorType, errorDesc);
      setError(errorDesc || errorType || 'Authentication failed');
      setVerifying(false);
      return;
    }
    
    const hasAuthTokens = combined.includes('access_token=') || 
                          combined.includes('token=') ||
                          combined.includes('type=magiclink');
    
    console.log('Has auth tokens in URL:', hasAuthTokens);
  }, []);

  // 🔴 BUG FIX #1: REMOVED PREMATURE URL CLEANUP
  // The useEffect that was here (lines 50-60) was cleaning auth tokens
  // from the URL before Supabase could process them with detectSessionInUrl.
  // This was the PRIMARY cause of magic link failures.
  // Supabase will handle URL cleanup automatically.

  useEffect(() => {
    // Set a timeout to show error if auth doesn't complete in 10 seconds
    const timeout = setTimeout(() => {
      if (!user && !authLoading) {
        console.warn('⏱️ Auth timeout - no user after 10 seconds');
        setAuthTimeout(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [user, authLoading]);

  useEffect(() => {
    const verifyAndSetup = async () => {
      console.log('🔄 verifyAndSetup running - authLoading:', authLoading, 'user:', !!user);
      
      // Wait for auth to complete
      if (authLoading) {
        console.log('⏳ Still loading auth...');
        return;
      }

      // If no user after auth completes, wait a bit more (auth might be processing)
      if (!user) {
        console.log('⚠️ No user found');
        // Only show error if we've waited long enough
        if (authTimeout) {
          console.error('❌ Auth timeout - showing error');
          setError("No user found. Please try signing in again.");
          setVerifying(false);
        }
        return;
      }

      console.log('✅ User authenticated:', user.email);

      try {
        // Check if user has completed onboarding
        console.log('🔍 Checking if user completed onboarding...');
        const completed = await hasCompletedOnboarding(user.id);

        if (completed) {
          // User already has profile and consent - go to discover
          console.log('✅ User already onboarded - redirecting to discover');
          toast.success("Welcome back!");
          setTimeout(() => {
            onNavigate && onNavigate("discover");
          }, 1000);
          return;
        }

        console.log('📝 Creating new user profile...');
        // Check if profile exists, if not create it
        const displayName = user.user_metadata?.display_name || user.email?.split('@')[0] || 'User';
        
        try {
          await createProfile(user.id, {
            display_name: displayName,
            role: 'patient',
          });
          console.log('✅ Profile created successfully');
        } catch (profileError: any) {
          // Profile might already exist, that's okay
          if (!profileError.message?.includes('duplicate') && !profileError.code?.includes('23505')) {
            console.error('❌ Profile creation error:', profileError);
            throw profileError;
          }
          console.log('ℹ️ Profile already exists');
        }

        setVerified(true);
        toast.success("Email verified!");

        console.log('➡️ Redirecting to consent page in 2 seconds...');
        // Redirect to consent page after 2 seconds
        setTimeout(() => {
          onNavigate && onNavigate("consent");
        }, 2000);

      } catch (err: any) {
        console.error("❌ Verification error:", err);
        setError(err.message || "Failed to verify email. Please try again.");
        toast.error("Verification failed");
      } finally {
        setVerifying(false);
      }
    };

    verifyAndSetup();
  }, [user, authLoading, onNavigate]); // 🔴 BUG FIX #4: Removed authTimeout to prevent race condition

  if (authLoading || verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-md">
          <Card className="p-10 glass-card text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>

            <h2 className="text-3xl mb-3">Verifying your email...</h2>

            <p className="text-muted-foreground">
              Please wait while we confirm your account
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="p-10 glass-card text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>

              <h2 className="text-3xl mb-3">Verification failed</h2>

              <p className="text-muted-foreground mb-6">{error}</p>

              <div className="space-y-3">
                <Button
                  className="w-full gradient-green hover:opacity-90 transition-opacity"
                  onClick={() => onNavigate && onNavigate("signin")}
                >
                  Back to Sign In
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-10 glass-card text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-16 h-16 rounded-full gradient-green flex items-center justify-center mx-auto mb-6 shadow-xl"
              >
                <CheckCircle className="w-8 h-8 text-white" />
              </motion.div>

              <h2 className="text-3xl mb-3">Email verified!</h2>

              <p className="text-muted-foreground mb-6">
                Your account has been successfully verified.
              </p>

              <div className="p-4 rounded-xl bg-accent/30 border border-primary/20 mb-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Redirecting you to complete consent and age verification...
                </p>
              </div>

              <Button
                className="w-full gradient-green hover:opacity-90 transition-opacity"
                onClick={() => onNavigate && onNavigate("consent")}
              >
                Continue
              </Button>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Fallback (shouldn't reach here)
  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="w-full max-w-md">
        <Card className="p-10 glass-card text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-xl gradient-green flex items-center justify-center shadow-lg">
              <Leaf className="w-7 h-7 text-white" />
            </div>
          </div>
          <h2 className="text-3xl mb-3">CannaBias</h2>
          <p className="text-muted-foreground mb-6">
            Loading your account...
          </p>
        </Card>
      </div>
    </div>
  );
}
